import extractQueryParamFromPageLocation from '@src/quince-site-common/src/utils/extractQueryParamFromPageLocation'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getCookieStorage } from '@src/utils/enhancedCookie'

/**
 * Twitter tracking constants
 * Based on Twitter Conversion Tracking documentation
 * https://business.x.com/en/help/campaign-measurement-and-analytics/conversion-tracking-for-websites
 */
export const TWITTER_TRACKING_CONSTANTS = {
  // Twitter adds twclid to destination URL when user clicks on Twitter ad
  CLICK_ID_URL_PARAM: 'twclid',
  CLICK_ID_COOKIE: '_quince_twclid', // Our own cookie, separate from Twitter's pixel
  // Twitter recommends storing twclid for attribution window (typically 30 days)
  COOKIE_EXPIRY_MINUTES: 30 * 24 * 60,
} as const

/**
 * Extracts query parameter from referrer URL
 */
const extractQueryParamFromReferrer = (key: string): string => {
  try {
    if (typeof window === 'undefined' || !window.document.referrer) {
      return ''
    }

    const url = new URL(window.document.referrer)
    return url.searchParams.get(key) ?? ''
  } catch (error) {
    return ''
  }
}

/**
 * Sets the _quince_twclid cookie when twclid URL parameter is present
 */
const setTwitterClickIdCookie = (twclid: string): void => {
  if (twclid && typeof window !== 'undefined') {
    const cookieStorage = getCookieStorage()
    cookieStorage.setCookieLocalized(TWITTER_TRACKING_CONSTANTS.CLICK_ID_COOKIE, twclid, {
      isGlobal: true,
      expires: new Date(Date.now() + TWITTER_TRACKING_CONSTANTS.COOKIE_EXPIRY_MINUTES * 60 * 1000),
      secure: true,
    })
  }
}

/**
 * Extracts Twitter Click ID (twclid) with proper priority
 * Priority: URL Parameter > Cookie > Referrer
 *
 * Twitter flow: URL parameter → Cookie → Our extraction
 */
export const getTwitterClickId = (): string => {
  // 1. First try URL parameter (Twitter adds twclid to destination URL)
  const urlClickId: string = extractQueryParamFromPageLocation(TWITTER_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (urlClickId) {
    // Set the cookie when we find the URL parameter
    setTwitterClickIdCookie(urlClickId)
    return urlClickId
  }

  // 2. Try primary Twitter cookie (for attribution from previous visits)
  const clickId = getCookie(TWITTER_TRACKING_CONSTANTS.CLICK_ID_COOKIE)
  if (clickId) {
    return clickId
  }

  // 3. Fallback to referrer (if available)
  const referrerClickId: string = extractQueryParamFromReferrer(TWITTER_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (referrerClickId) {
    // Set the cookie when we find the referrer parameter
    setTwitterClickIdCookie(referrerClickId)
    return referrerClickId
  }

  return ''
}

/**
 * Gets Twitter tracking data
 */
export const getTwitterTrackingData = (): {
  twclid: string
} => {
  return {
    twclid: getTwitterClickId(),
  }
}
